/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { useCallback } from "react";

import { TFunction } from "i18next";
import Separator from "@/components/Separator/Separator";

import { colors } from "@dotnet/telematics-design-system";

import GoBack from "../../../../components/Actions/GoBack/GoBack";
import RectangleLoader from "../../../../shared/components/loaders/RectangleLoader";
import ChartListItem from "../../components/ChartListItem/ChartListItem";
import HorizontalBarChart from "../../LeftSide/ResourceDownloads/HorizontalBarChart/HorizontalBarChart";
import { ConnectivityStatusListType, StatusContentType } from "../../types";
import DeactivatedVehiclesButton from "../DeviceStatus/components/DeactivatedVehiclesButton/DeactivatedVehiclesButton";
import DoughnutChart from "../DeviceStatus/components/DoughnutChart/DoughnutChart";
import SingleStatusInfo from "../DeviceStatus/components/SingleStatusInfo/SingleStatusInfo";
import {
  hoveredElementToRealIndex,
  statusBorderColor,
  statusColor
} from "../DeviceStatus/functions";
import { StatusSummaryType } from "../DeviceStatus/types";

interface UseConnectivityStatusProps {
  t: TFunction;
}

const useChartElements = ({ t }: UseConnectivityStatusProps) => {
  const generateBackButton = useCallback(
    (handleGoBack: () => void) => {
      return <GoBack onGoBack={handleGoBack} text={t("Actions##back")} />;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const generateStatusList = ({
    statusData,
    hoveredElement,
    hovered,
    hoveredListItem,
    setShowSingleStatusInfo,
    setHoveredListItem
  }: ConnectivityStatusListType) => {
    if (!statusData) return;

    return statusData.summary.map((item: StatusSummaryType, index: number) => {
      const isHoveredOnChart =
        index === hoveredElementToRealIndex(hoveredElement) && hovered;

      const isHoveredOnItem = item.name === hoveredListItem;

      return (
        <ChartListItem
          key={item.name}
          item={item}
          hoveredElement={hoveredElement}
          hovered={isHoveredOnChart || isHoveredOnItem}
          backgroundColor={statusColor}
          borderColor={statusBorderColor}
          setShowSingleStatusInfo={setShowSingleStatusInfo}
          isClickable
          testId={`doughnut-chart-list-item-${item.name}`}
          setHoveredListItem={setHoveredListItem}
          hoveredListItem={hoveredListItem}
        />
      );
    });
  };

  const generateStatusContent = ({
    isLoading,
    showSingleChartItemInfo,
    showListItemsInfo,
    chartContainer,
    statusData,
    showSingleStatusInfo,
    hasCardWrapper = false,
    setHovered,
    setHoveredElement,
    setShowSingleStatusInfo,
    chartListBox,
    generateList,
    activeTab
  }: StatusContentType) => (
    <>
      {isLoading && (
        <RectangleLoader width="100%" height="100%" testId="status-loader" />
      )}
      {!isLoading && showSingleChartItemInfo ? (
        <SingleStatusInfo
          infoData={showSingleStatusInfo as StatusSummaryType}
          hasCardWrapper={hasCardWrapper}
          activeTab={activeTab}
        />
      ) : (
        !isLoading &&
        showListItemsInfo && (
          <div>
            <div css={css(chartContainer as CSSObject)}>
              <DoughnutChart
                statusData={statusData}
                setHovered={setHovered}
                setHoveredElement={setHoveredElement}
                setShowSingleStatusInfo={setShowSingleStatusInfo}
                activeTab={activeTab}
              />
              <div
                css={css([
                  chartListBox as CSSObject,
                  { justifyContent: "flex-start" }
                ])}
              >
                {generateList(statusData)}
              </div>
            </div>
            {statusData?.deactivatedSummary && (
              <>
                <Separator
                  css={css({
                    marginBottom: "16px",
                    "@media (max-width: 599px)": {
                      margin: "16px 0"
                    },
                    backgroundColor: colors.gray200
                  })}
                />
                <DeactivatedVehiclesButton
                  summary={statusData.deactivatedSummary}
                  setShowSingleStatusInfo={setShowSingleStatusInfo}
                />
              </>
            )}
          </div>
        )
      )}
    </>
  );

  const generateResourceDownloadsStatusContent = ({
    isLoading,
    showSingleChartItemInfo,
    showListItemsInfo,
    chartContainer,
    statusData,
    showSingleStatusInfo,
    resourceDownloadLocation,
    setHovered,
    setHoveredElement,
    setShowSingleStatusInfo,
    chartListBox,
    generateList
  }: StatusContentType) => (
    <>
      {isLoading && (
        <RectangleLoader
          width="100%"
          height="100%"
          testId="downloads-status-loader"
        />
      )}
      {!isLoading && showSingleChartItemInfo ? (
        <SingleStatusInfo
          resourceDownloadLocation={resourceDownloadLocation}
          infoData={showSingleStatusInfo as StatusSummaryType}
          hasCardWrapper={false}
        />
      ) : (
        !isLoading &&
        showListItemsInfo && (
          <div css={css(chartContainer as CSSObject)}>
            <HorizontalBarChart
              statusData={statusData}
              setHovered={setHovered}
              setHoveredElement={setHoveredElement}
              setShowSingleStatusInfo={setShowSingleStatusInfo}
            />
            <div css={css([chartListBox as CSSObject])}>
              {generateList(statusData)}
            </div>
          </div>
        )
      )}
    </>
  );

  return {
    generateStatusList,
    generateBackButton,
    generateStatusContent,
    generateResourceDownloadsStatusContent
  };
};

export default useChartElements;
